/**
 * Field Mode.
 *
 * Directive §23 preserves: team, current stop number, progress, address, city,
 * ETA, Plan for, records at location, per-record Call, Navigate, Arrived,
 * Skip this stop, the next-segment notice at a route boundary, and the outcome
 * choices.
 *
 * Directive §6: the mobile bottom navigation is hidden here.
 * Directive §22 / §70: for a PROTECTED trip the external-navigation controls are
 * REPLACED with the policy message. The server has already refused to generate a
 * URL — this component renders that refusal. Hiding a button is not a security
 * control, and no URL exists on the client to hide.
 */

import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

export interface FieldRecord {
  id: string;
  name: string;
  /** Null when the record has no phone on file. */
  phone: string | null;
  selected: boolean;
}

export interface FieldStop {
  stopId: string;
  routePlanId?: string;
  teamName: string;
  /** 1-based. */
  stopNumber: number;
  totalStops: number;
  completedStops: number;
  addressLine: string;
  cityLine: string;
  etaLabel: string;
  planForLabel: string;
  records: FieldRecord[];
  /** Server-generated, just in time, LOGISTICS only. Null for PROTECTED. §21 §80 */
  navigationUrl: string | null;
  appleNavigationUrl: string | null;
  /** Policy copy when navigation is refused. §22 */
  navigationBlockedMessage: string | null;
  /** Shown at a route-segment boundary. §23 */
  nextSegmentNotice: string | null;
}

export function FieldModeScreen({
  stop, onCall, onToggleRecord, onArrived, onSkip, onExit,
}: {
  stop: FieldStop | null;
  onCall: (recordId: string) => void;
  onToggleRecord: (recordId: string) => void;
  onArrived: () => void;
  onSkip: () => void;
  onExit: () => void;
}) {
  if (!stop) {
    return (
      <div className="pt-8 max-w-[520px]">
        <h1 className="mb-2">Route complete</h1>
        <p className="text-base text-neutral-700 mb-6">
          Every stop in this route has been handled.
        </p>
        <button className="btn btn-primary btn-field" onClick={onExit}>
          Return to Visits
        </button>
      </div>
    );
  }

  const blocked = stop.navigationUrl === null;

  return (
    <div className="pt-4 pb-8">
      <div className="flex items-baseline gap-3 mb-3">
        <h6 className="text-accent-700">
          {stop.teamName} · Stop {stop.stopNumber} of {stop.totalStops}
        </h6>
        <span className="ml-auto text-sm text-neutral-700 tabular-nums">
          {stop.completedStops} of {stop.totalStops} done
        </span>
      </div>

      <h1 className="mb-1">{stop.addressLine}</h1>
      <p className="text-base text-neutral-700 mb-6">{stop.cityLine}</p>

      <div className="flex gap-8 mb-6">
        <div>
          <h6 className="text-accent-700 mb-1">ETA</h6>
          <p className="font-heading font-semibold text-h3 tabular-nums">{stop.etaLabel}</p>
        </div>
        <div>
          <h6 className="text-accent-700 mb-1">Plan for</h6>
          <p className="font-heading font-semibold text-h3 tabular-nums">{stop.planForLabel}</p>
        </div>
      </div>

      <h6 className="text-accent-700 mb-2">
        {stop.records.length} {stop.records.length === 1 ? 'record' : 'records'} at this location
      </h6>

      {/* A shared stop carries several records; the operator selects which the
          outcome applies to. With one record there is nothing to choose. §19 §23 */}
      {stop.records.length > 0 && (
        <ul className="mb-4">
          {stop.records.map((r) => (
            <li key={r.id} className="border-b border-neutral-300">
              <label className="flex items-center gap-3 py-3 min-h-touch cursor-pointer">
                {stop.records.length > 1 && <input
                    type="checkbox"
                    checked={r.selected}
                    onChange={() => onToggleRecord(r.id)}
                    className="w-5 h-5 accent-accent"
                  />}
                <span className="flex-1 font-heading text-base">{r.name}</span>
                {r.phone && (
                  <button
                    onClick={(e) => { e.preventDefault(); onCall(r.id); }}
                    className="btn btn-ghost"
                  >
                    Call
                  </button>
                )}
              </label>
            </li>
          ))}
        </ul>
      )}

      {stop.nextSegmentNotice && (
        <p className="mb-4 p-3 bg-accent-100 border border-accent-300 rounded-lg text-base">
          {stop.nextSegmentNotice}
        </p>
      )}

      <div className="grid grid-cols-2 gap-3 mb-3">
        <button
          className="btn btn-secondary btn-field"
          onClick={() => onCall(stop.records.find((record) => record.phone)?.id ?? '')}
          disabled={!stop.records.some((r) => r.phone)}
        >
          Call
        </button>

        {blocked ? (
          // §22: the control is REPLACED, not hidden. There is no URL here to
          // reveal — the API refused to generate one. §70
          <div
            className="btn btn-field bg-neutral-200 text-neutral-700 cursor-not-allowed
                       text-sm leading-tight px-2"
            role="note"
          >
            {stop.navigationBlockedMessage ??
              'External navigation unavailable for this protected trip.'}
          </div>
        ) : (
          <a
            href={stop.navigationUrl!}
            target="_blank"
            // §90: no-referrer on sensitive external navigation.
            rel="noreferrer noopener"
            className="btn btn-secondary btn-field"
          >
            Navigate
            <ArrowTopRightOnSquareIcon className="w-4 h-4" aria-hidden />
          </a>
        )}
      </div>

      {!blocked && stop.appleNavigationUrl && (
        <p className="text-center mb-4">
          <a
            href={stop.appleNavigationUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="text-base text-accent-700 underline min-h-touch inline-flex items-center"
          >
            Open in Apple Maps instead
          </a>
        </p>
      )}

      <button className="btn btn-primary btn-field mb-3" onClick={onArrived}>
        Arrived
      </button>

      <button className="btn btn-ghost btn-block" onClick={onSkip}>
        Skip this stop
      </button>

      <button className="btn btn-ghost btn-block mt-6" onClick={onExit}>
        Leave field mode
      </button>
    </div>
  );
}

/**
 * Field outcome sheet. §23 keeps exactly these five choices.
 * Completion writes a field-visit Interaction for every selected record, then
 * advances automatically — the advance is the server's response, not a
 * client-side assumption.
 */
export const FIELD_OUTCOMES = [
  { id: 'completed', label: 'Completed' },
  { id: 'no_answer', label: 'No Answer' },
  { id: 'unable_to_access', label: 'Unable to Access' },
  { id: 'reschedule', label: 'Reschedule' },
  { id: 'other', label: 'Other' },
] as const;

export type FieldOutcomeId = (typeof FIELD_OUTCOMES)[number]['id'];

export function FieldOutcomeBody({
  outcome, note, recordCount, onOutcomeChange, onNoteChange,
}: {
  outcome: FieldOutcomeId | null;
  note: string;
  recordCount: number;
  onOutcomeChange: (o: FieldOutcomeId) => void;
  onNoteChange: (n: string) => void;
}) {
  return (
    <div>
      <fieldset>
        <legend className="text-h6 uppercase text-neutral-700 mb-2">Outcome</legend>
        <div className="flex flex-wrap gap-2 mb-4">
          {FIELD_OUTCOMES.map((o) => (
            <button
              key={o.id}
              onClick={() => onOutcomeChange(o.id)}
              aria-pressed={outcome === o.id}
              className={`seg ${outcome === o.id ? 'seg-active' : ''}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </fieldset>

      <label className="field">
        <span>Note</span>
        <textarea
          className="input min-h-[96px]"
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          placeholder="Optional"
        />
      </label>

      <p className="text-sm text-neutral-700">
        Applies to {recordCount} {recordCount === 1 ? 'record' : 'records'} at this location.
      </p>
    </div>
  );
}
